import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import SelectConstructionCategory from './select-construction-category/select-construction-category.component';
import ConstructionForm from './construction-form/construction-form.component';
import { SimpleSubCategory } from 'models/order-input/simple-order';
import { RestorationInput } from 'models/order-input/restoration-input';
import Warranty from 'components/lab-order/warranty/warranty.component';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { SimpleOrderContext } from 'pages/order-flow/simple-order/simple-order-context';
import { SupplierOrderContext } from 'pages/order-flow/supplier-order/supplier-order-context';
import TandvardCheckbox from 'components/order-flow/action-order/constructions-step/constructions/tandvard-checkbox/tandvard-checkbox.component';
import { simpleCategoryForType, warrantyTypeOfInput } from 'components/order-flow/shared/restoration-input-helper';

type AddConstructionDialogProps = {
    open: boolean;
    onClose: () => void;
    /** The restoration to edit; the dialog adds a new one without it. */
    restoration?: RestorationInput;
    onSave?: (restoration: RestorationInput) => void;
};
const AddConstructionDialog: FC<AddConstructionDialogProps> = ({ open, onClose, restoration, onSave }) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const { order } = useContext(SimpleOrderContext);
    const { order: supplierOrder } = useContext(SupplierOrderContext);
    const [activeStep, setActiveStep] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState<SimpleSubCategory>();
    const [warranty, setWarranty] = useState<boolean>(false);
    const [warrantyType, setWarrantyType] = useState<string>('');
    const [isTioTandvard, setIsTioTandvard] = useState<boolean>(false);
    const [currentRestoration, setCurrentRestoration] = useState<RestorationInput>();

    const lab = order?.supplier ?? supplierOrder?.supplier;
    const isTioTandvardPricingForLabEnabled = customerSettings.getCustomerSetting(
        lab?.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING_FOR_LAB,
    );

    useEffect(() => {
        if (open && restoration) {
            setSelectedCategory(simpleCategoryForType(restoration.type));
            setActiveStep(1);
            setWarranty(Boolean(restoration.warranty));
            setWarrantyType(warrantyTypeOfInput(restoration) ?? '');
            setIsTioTandvard(Boolean(restoration.isTioTandvard));
        }
    }, [open, restoration]);

    const handleCategorySelected = (category: SimpleSubCategory) => {
        setSelectedCategory(category);
        setActiveStep(1);
    };

    const handleClose = () => {
        onClose();
        // Prevent glitch when closing dialog
        setTimeout(() => {
            setCurrentRestoration(undefined);
            setSelectedCategory(undefined);
            setActiveStep(0);
            setWarranty(false);
            setWarrantyType('');
            setIsTioTandvard(false);
        }, 500);
    };

    const handleSaveButtonClicked = () => {
        if (currentRestoration && onSave) {
            const properties = currentRestoration.properties.filter((property) => property.key !== 'WARRANTY_TYPE');
            onSave({
                ...currentRestoration,
                warranty,
                isTioTandvard,
                properties:
                    warranty && warrantyType && warrantyType !== 'NONE'
                        ? [...properties, { key: 'WARRANTY_TYPE', value: warrantyType }]
                        : properties,
            });
        }
        handleClose();
    };

    const onHandleWarranty = (warranty: boolean, warrantyType: string) => {
        setWarranty(warranty);
        setWarrantyType(warrantyType);
    };

    const getStepContent = () => {
        switch (activeStep) {
            case 0:
                return <SelectConstructionCategory onCateogrySelected={handleCategorySelected} />;
            case 1:
                return selectedCategory ? (
                    <ConstructionForm
                        category={selectedCategory}
                        restoration={restoration}
                        onRestorationChange={setCurrentRestoration}
                    />
                ) : undefined;
            default:
                return 'Unknown stepIndex';
        }
    };

    const title = () => {
        if (restoration) return translate('orders:MODIFY_CONSTRUCTION');
        return selectedCategory ? translate(`orders:${selectedCategory}`) : translate('orders:ADD_CONSTRUCTION');
    };

    return (
        <DHDialog
            open={open}
            onClose={handleClose}
            disableBackdropClick
            maxWidth="xl"
            fullWidth
            title={title()}
            actions={
                <>
                    {isTioTandvardPricingForLabEnabled && (
                        <Box mr={1}>
                            <TandvardCheckbox handleTandvardCheckbox={setIsTioTandvard} isTioTandvard={isTioTandvard} />
                        </Box>
                    )}
                    <Warranty handleWarranty={onHandleWarranty} warranty={warranty} warrantyType={warrantyType} />
                    <Button
                        variant="contained"
                        disabled={!currentRestoration}
                        onClick={handleSaveButtonClicked}
                        data-test="add-construction-dialog.save-btn"
                    >
                        {restoration ? translate('common:SAVE') : translate('orders:ADD_CONSTRUCTION')}
                    </Button>
                </>
            }
        >
            <Box>{getStepContent()}</Box>
        </DHDialog>
    );
};

export default AddConstructionDialog;
